import { format, parse, isValid } from 'date-fns';
import { enUS, enGB, enAU, fr, frCA, de } from "date-fns/locale";
import configs from '../config/index.json';
import i18n from "../i18n";
import { getUserLocale } from "./common";

const LOCALE_MAP = {
  "en-US": enUS,
  "en-GB": enGB,
  "en-AU": enAU,
  "fr-FR": fr,
  "fr-CA": frCA,
  "de-DE": de,
};

export const msToDuration = ({ milliseconds, fmt = 'mm:ss' }) => {
  const date = new Date();
  date.setHours(0, 0, 0, milliseconds);
  return format(date, fmt);
};

export const dateParser = (dateString, dateFormat) => {
  let result = dateString;
  try {
    let parsedDate = new Date(dateString);

    if (isValid(parsedDate)) {
      result = format(parsedDate, dateFormat);
    } else {
      parsedDate = parse(dateString, configs.defaultDateFormat, new Date());
      if (isValid(parsedDate)) {
        result = format(parsedDate, dateFormat);
      } else {
        const parsedDate1 = parse(dateString, 'MM/dd/yyyy HH:mm:ss.S', new Date());
        result = format(parsedDate1, dateFormat);
      }
    }
  } catch (error) {
    console.error('Error parsing date:', error);
  }
  return result;
};

export const formatDateForDraftsAPI = (date) => {
  
  if (!date) return null;
  
  let year, month, day;
  
  if (date instanceof Date) {
    year = date.getFullYear();
    month = date.getMonth();
    day = date.getDate();
  } else if (typeof date === 'string') {
    // Remove any existing time from the string for parsing
    const dateOnly = date.split(' ')[0];
    
    // Check for DD-MM-YYYY format
    const ddmmMatch = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(dateOnly);
    if (ddmmMatch) {
      const [, dayStr, monthStr, yearStr] = ddmmMatch;
      year = parseInt(yearStr, 10);
      month = parseInt(monthStr, 10) - 1;
      day = parseInt(dayStr, 10);
    } else {
      // Try parsing as standard date string
      const tempDate = new Date(date);
      if (isNaN(tempDate.getTime())) {
        console.error('Invalid date string:', date);
        return null;
      }
      year = tempDate.getFullYear();
      month = tempDate.getMonth();
      day = tempDate.getDate();
    }
  } else {
    console.error('Date is not a Date object or string:', date);
    return null;
  }
  
  const dateObj = new Date(year, month, day);
  
  if (isNaN(dateObj.getTime())) {
    console.error('Invalid date object created from:', { year, month, day });
    return null;
  }
  
  const dayFormatted = String(dateObj.getDate()).padStart(2, '0');
  const monthFormatted = String(dateObj.getMonth() + 1).padStart(2, '0');
  const yearFormatted = dateObj.getFullYear();
  
  // Build the formatted date
  const formattedDate = `${dayFormatted}-${monthFormatted}-${yearFormatted}`;

  return formattedDate;
};

const parseDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;
  
  let date = new Date(dateTimeString);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  const ddmmyyyyRegex = /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/;
  let match = ddmmyyyyRegex.exec(dateTimeString);
  if (match) {
    const [, day, month, year, hours, minutes, seconds] = match;
    date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds, 10)
    );
    if (!isNaN(date.getTime())) return date;
  }
  
  const mmddyyyyRegex = /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/;
  match = mmddyyyyRegex.exec(dateTimeString);
  if (match) {
    const [, month, day, year, hours, minutes, seconds] = match;
    date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds, 10)
    );
    if (!isNaN(date.getTime())) return date;
  }
  
  console.warn('Unable to parse date:', dateTimeString);
  return null;
};

export const processDraftsData = (draftsData, filters = {}, displayDateFormat = 'dd/MM/yyyy') => {
  if (!Array.isArray(draftsData)) return [];

  return draftsData
    .filter(draft => {
      if (!filters.minDate && !filters.maxDate) return true;
      
      const originalDateTime = draft['last-updated-time'];
      if (!originalDateTime) return false;
      
      const targetDate = parseDateTime(originalDateTime);
      if (!targetDate) return false;
      
      // Check min date
      if (filters.minDate) {
        const minDate = new Date(filters.minDate);
        minDate.setHours(0, 0, 0, 0);
        if (targetDate < minDate) return false;
      }
      
      // Check max date
      if (filters.maxDate) {
        const maxDate = new Date(filters.maxDate);
        maxDate.setHours(23, 59, 59, 999);
        if (targetDate > maxDate) return false;
      }
      
      return true;
    })
    .map(draft => {
      // Keep the original value from backend as is
      return {
        ...draft
      };
    });
};

// Helper function to format dates for API calls when from/to dates are involved
export const formatDateRangeForAPI = (fromDate, toDate) => {
  if (!fromDate && !toDate) {
    return { from: null, to: null };
  }
  
  // If only fromDate exists
  if (fromDate && !toDate) {
    return {
      from: formatDateForDraftsAPI(fromDate),
      to: null
    };
  }
  
  // If only toDate exists
  if (!fromDate && toDate) {
    // Add 1 day to toDate
    const toDateObj = new Date(toDate);
    toDateObj.setDate(toDateObj.getDate() + 1);
    
    return {
      from: null,
      to: formatDateForDraftsAPI(toDateObj)
    };
  }
  
  // Both dates exist - check if they're the same day
  const fromDateStr = typeof fromDate === 'string' ? fromDate.split(' ')[0] : fromDate;
  const toDateStr = typeof toDate === 'string' ? toDate.split(' ')[0] : toDate;
  
  const fromDateObj = new Date(fromDateStr);
  const toDateObj = new Date(toDateStr);
  
  // Check if dates are the same day
  const isSameDay = fromDateObj.toDateString() === toDateObj.toDateString();
  
  if (isSameDay) {
    // For same day, add 1 day to toDate
    const nextDay = new Date(toDateObj);
    nextDay.setDate(nextDay.getDate() + 1);
    
    return {
      from: formatDateForDraftsAPI(fromDate),
      to: formatDateForDraftsAPI(nextDay)
    };
  }
  
  // Different days - add 1 day to toDate
  const nextDay = new Date(toDateObj);
  nextDay.setDate(nextDay.getDate() + 1);
  
  return {
    from: formatDateForDraftsAPI(fromDate),
    to: formatDateForDraftsAPI(nextDay)
  };
};

export const parseStringToDate = (dateString, dateFormat = configs.defaultDateFormat) => {
  let result = null;
  try {
    result = parse(dateString, dateFormat, new Date()) ? parse(dateString, dateFormat, new Date()) : new Date(dateString);
  } catch (error) {
    result = new Date(dateString);
  }
  return result;
};

export const parseDateToString = (date, dateFormat = configs.defaultDateFormat) => {
  let result = '-';
  if (typeof date === 'object' && date instanceof Date) {
    result = format(date, dateFormat);
  }
  return result;
};

export function formatYear(date, locale) {
  return format(date, "yyyy", { locale });
}

export function formatFullDate(date, locale) {
  return format(date, "EEE, MMM d", { locale });
}

export const getDateLocale = () => {
  const localeKey = getUserLocale(i18n?.language);
  return LOCALE_MAP[localeKey] || enUS;
};

export default null;